import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { routerActions } from "react-router-redux";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { ProjectItemImportBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as NavigationActions from "../../../actions/navigation";
import * as ToggleActions from "../../../actions/pages/togglePage";
import * as ProjectItemImportActions from "../../../actions/projectItemImport";
import { NotificationAlert } from "../../../components/common/index";
import { SelectFormInput } from "../../../components/form/index";
import { TruncatedCell } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import {
    renderRegistrationFormSaveBlock,
    renderRegistrationFormSaveSuccess,
    renderRegistrationFormSaveWarning,
} from "../../../helpers/alert";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import * as ToggleHelper from "../../../helpers/toggleHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { TogglePageItem } from "../../../reducers/pages/togglePage";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { IProjectItemImportReduxFormState } from "../../../reducers/reactReduxForms/projectItemImport";
import { ProjectLookupsLayout } from "../../index";
import "./ProjectItemImportPage.scss";

interface IProjectItemImportPageParams {}

interface IProjectItemImportPageProps
    extends IRouterProps<IProjectItemImportPageParams> {
    form: Dtos.ProjectItemImport & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IProjectItemImportReduxFormState;
    lookups: Dtos.Lookup[];
    validationFailures: Dtos.ResponseError[];
    selectedItems: number[];
    categories: Dtos.ProjectItemTemplateCategory[];

    projectId: number;
    sourceProjectId: number;

    // From state
    projectItemImport: Dtos.ProjectItemImport;
    loading: boolean;
    loadSuccess: boolean;
    loadFailure: boolean;

    saving: boolean;
    saveSuccess: boolean;
    saveFailure: boolean;

    user: Dtos.User;

    toggleItems: TogglePageItem[];
}

interface IProjectItemImportPageActions extends IRouterActions {
    clear: ProjectItemImportActions.IProjectItemImportClearActionCreator;
    load: ProjectItemImportActions.IProjectItemImportLoadByIdActionCreator;
    save: ProjectItemImportActions.IProjectItemImportSaveActionCreator;

    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    toggleExpand: ToggleActions.IToggleExpandedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

type ProjectItemImportPageProps = IProjectItemImportPageProps &
    IProjectItemImportPageActions;

const reduxFormName: string = "reduxForms.projectItemImport";
const tableHeaderClass = "btn btn-primary btn-block rounded-0 p-3 d-flex";
const tableStyleClass: React.CSSProperties = {
    textAlign: "left",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
};

class ProjectItemImportPage extends React.PureComponent<
    ProjectItemImportPageProps,
    any
> {
    initialised: boolean;

    constructor(props: ProjectItemImportPageProps) {
        super(props);
        this.initialised = false;
        this.clear = this.clear.bind(this);
        this.onCancel = this.onCancel.bind(this);
    }

    componentDidMount() {
        const {
            load,
            projectId,
            projectItemImport,
            setDrawerItems,
            setBreadcrumbs
        } = this.props;
        

        setDrawerItems([]);
        setBreadcrumbs(ProjectItemImportBreadcrumbs());

        if (projectId == undefined || projectId == 0) {
            this.onCancel();
        } else if (projectItemImport) {
            load(projectId, projectItemImport.projectId);
        } else {
            load(projectId, 0);
        }
    }

    componentDidUpdate(prevProps: ProjectItemImportPageProps, prevState) {
        const {
            form,
            projectId,
            sourceProjectId,
            load,
            loading,
            loadSuccess,
            saving,
            saveSuccess,
            validationFailures,
            navigate,
            setDrawerItems,
            setBreadcrumbs,
        } = this.props;

        if (!this.initialised) {
            setDrawerItems([]);
            setBreadcrumbs(ProjectItemImportBreadcrumbs());
            this.initialised = true;
        }

        if (projectId && prevProps.projectId != projectId) {
            load(projectId, sourceProjectId);
        }

        if (!loading && prevProps.loading) {
            if (loadSuccess) {
                this.loadForm();
            }
        }

        if (!saving && prevProps.saving) {
            if (saveSuccess) {
                if (validationFailures && validationFailures.length > 0) {
                    Alert.warning(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveWarning(
                                validationFailures
                            )}
                            icon={FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE}
                        />
                    );
                } else {
                    Alert.success(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveSuccess()}
                            icon={FontAwesomeIcons.Solid.CHECK}
                        />
                    );
                }
                if (form.saveAndReturn) {
                    navigate("/settings/project/itemtemplates");
                } else {
                    this.loadForm();
                }
            } else {
                Alert.error(
                    <NotificationAlert
                        alertContent={this.renderErrorAlert()}
                        icon={FontAwesomeIcons.Solid.TIMES_OCTAGON}
                    />
                );
            }
        }
    }

    componentWillUnmount() {
        this.clear();
    }

    clear() {
        this.props.clear();
    }

    show(): boolean {
        const { loading, loadSuccess } = this.props;

        if (!loading && loadSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const { loading } = this.props;

        return (
            <ProjectLookupsLayout
                loading={loading}
                permission={Dtos.Permission.ProjectItemTemplatesView}
            >
                {this.renderContent()}
            </ProjectLookupsLayout>
        );
    }

    renderContent() {
        const {
            form,
            lookups,
            validationFailures,
            formProperties,
            formState,
            changeForm,
            projectId,
            selectedItems,
            load,
            saving,
            resetForm,
        } = this.props;

        const formDisabled: boolean =
            saving ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        const formProjectSelected: boolean =
            !formDisabled && form.projectId && form.projectId > 0;
        const formCanSave: boolean =
            !formDisabled &&
            formProjectSelected &&
            selectedItems &&
            selectedItems.length > 0;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            id: {
                type: RegistrationComponent.RegistrationControl,
                inputType: SelectFormInput,
                inputDisabled: true,
                inputProps: {
                    options: generateOptionsFromLookup(
                        findLookup("ProjectItemImport", "Id", lookups),
                        form.id
                    ),
                },
                inputLabel: "Destination Project",
            },
            projectId: {
                type: RegistrationComponent.RegistrationControl,
                inputType: SelectFormInput,
                inputDisabled: formDisabled,
                inputProps: {
                    options: generateOptionsFromLookup(
                        findLookup("ProjectItemImport", "ProjectId", lookups),
                        form.projectId
                    ),
                },
                inputLabel: "Select project to import from",
                changeAction: (model, value, form, parentModel) => {
                    changeForm(model, value);
                    if (value != form.projectId) {
                        resetForm(reduxFormName);
                        load(projectId, value);
                    }
                },
            },
        };

        return (
            <div className="trail-item-import">
                <h3>Project Item Import</h3>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, event) => this.handleOnSubmit(val)}
                    onSubmitFailed={(val) => this.handleOnSubmitFailed(val)}
                >
                    <div className="container-fluid">
                        {renderFormControls(
                            form,
                            "projectItemImport",
                            formComponents,
                            lookups,
                            validationFailures,
                            formProperties,
                            changeForm
                        )}
                    </div>
                    {this.show() && (
                        <div className="mt-2">{this.renderTable()}</div>
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={!formCanSave}
                            onClick={() => {
                                this.props.changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={!formCanSave}
                            onClick={() => {
                                this.props.changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={this.onCancel}
                            className="btn btn-primary mr-2"
                        >
                            {" "}
                            Cancel{" "}
                        </button>
                    </div>
                </Form>
            </div>
        );
    }

    renderTable() {
        const { projectItemImport, categories, toggleItems, toggleExpand } =
            this.props;

        if (!categories || categories.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no items to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = categories.length > DEFAULT_PAGE_SIZE;
        const projectItemImportId = projectItemImport
            ? projectItemImport.id
            : 0;

        return (
            <div>
                {categories.map((item, index) => {
                    const key =
                        "project-items-page-" +
                        item.id +
                        "-" +
                        projectItemImportId;
                    const expanded = ToggleHelper.isToggleExpanded(
                        toggleItems,
                        key
                    );
                    return (
                        <div key={"project-items-category" + item.id}>
                            <div
                                className={tableHeaderClass}
                                style={tableStyleClass}
                                onClick={() => toggleExpand(key)}
                            >
                                <div>
                                    <FontAwesomeIcon
                                        icon={
                                            !expanded
                                                ? FontAwesomeIcons.Solid
                                                      .ANGLE_DOWN
                                                : FontAwesomeIcons.Solid
                                                      .ANGLE_UP
                                        }
                                        fixedWidth
                                        transform="grow-16"
                                        className="mr-3"
                                    />
                                </div>
                                <span>{item.value}</span>
                            </div>
                            {expanded && (
                                <ReactTable
                                    data={item.projectItemTemplates}
                                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                                    defaultPageSize={
                                        showPagination
                                            ? DEFAULT_PAGE_SIZE
                                            : item.projectItemTemplates.length
                                    }
                                    pageSize={
                                        showPagination
                                            ? undefined
                                            : item.projectItemTemplates.length
                                    }
                                    showPagination={showPagination}
                                    className="-striped -highlight -clickable"
                                    noDataText="There are currently no items for this category"
                                    resizable={false}
                                    getTdProps={(
                                        state,
                                        rowInfo: RowInfo,
                                        _column,
                                        _instance
                                    ) => {
                                        return {
                                            onClick: (e, handleOriginal) => {
                                                this.onSelectedItemChanged(
                                                    rowInfo.original.id,
                                                    !this.isItemSelected(
                                                        rowInfo.original.id
                                                    )
                                                );

                                                if (handleOriginal) {
                                                    handleOriginal();
                                                }
                                            },
                                        };
                                    }}
                                    columns={[
                                        {
                                            id: "id",
                                            Header: " ",
                                            accessor: "id",
                                            maxWidth: 50,
                                            Cell: (props) => (
                                                <FontAwesomeIcon
                                                    size="lg"
                                                    icon={
                                                        this.isItemSelected(
                                                            props.value
                                                        )
                                                            ? FontAwesomeIcons
                                                                  .Light.CHECK
                                                            : FontAwesomeIcons
                                                                  .Light
                                                                  .UNCHECKED
                                                    }
                                                    fixedWidth
                                                />
                                            ),
                                        },
                                        {
                                            id: "id",
                                            Header: " ",
                                            accessor: "id",
                                            maxWidth: 50,
                                            Cell: (_props) => (
                                                <FontAwesomeIcon
                                                    size="lg"
                                                    icon={
                                                        FontAwesomeIcons.Light
                                                            .TRIALITEM
                                                    }
                                                    fixedWidth
                                                />
                                            ),
                                        },
                                        {
                                            id: "name",
                                            Header: "Name",
                                            accessor: "name",
                                            Cell: (props) => (
                                                <TruncatedCell
                                                    value={props.value}
                                                />
                                            ),
                                        },
                                        {
                                            id: "description",
                                            Header: "Description",
                                            accessor: "description",
                                            Cell: (props) => (
                                                <TruncatedCell
                                                    value={props.value}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderErrorAlert(): any {
        const { validationFailures } = this.props;

        return (
            <div>
                The form was <strong> not </strong> saved.
            </div>
        );
    }

    renderWarningAlert(): any {
        const { validationFailures } = this.props;

        return (
            <div>
                The form was saved but was invalid with{" "}
                {validationFailures.length} errors.
            </div>
        );
    }

    renderSaveSuccessAlert(): any {
        return (
            <div>The items hves been successfully saved to the template</div>
        );
    }

    handleOnSubmit(data: Dtos.ProjectItemImport) {
        const { save } = this.props;

        if (data) {
            let changes: Dtos.ProjectItemImport = {} as Dtos.ProjectItemImport;
            save(Object.assign({ ...data }, changes));
        }
    }

    handleOnSubmitFailed(_data: Dtos.ProjectItemImport) {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    onSelectedItemChanged(id: number, selected: boolean) {
        const { selectedItems, changeForm } = this.props;

        if (selectedItems) {
            let newArray = [...selectedItems];
            var index = newArray.indexOf(id);
            if (selected) {
                // add if not exists
                if (index == -1) {
                    newArray.push(id);
                    changeForm(reduxFormName + ".selectedItems", newArray);
                }
            } else {
                // remove if not exists
                if (index > -1) {
                    newArray.splice(index, 1);
                    changeForm(reduxFormName + ".selectedItems", newArray);
                }
            }
        }
    }

    isItemSelected(id: number) {
        const { selectedItems } = this.props;

        if (selectedItems) {
            var index = selectedItems.indexOf(id);
            return index > -1;
        }
        return false;
    }

    onCancel() {
        const { projectId, navigate } = this.props;

        navigate("/settings/project/itemtemplates");
    }

    loadForm() {
        const { loadForm, projectItemImport } = this.props;

        let extraData: Dtos.ProjectItemImport = {} as Dtos.ProjectItemImport;

        loadForm(
            reduxFormName,
            Object.assign({ ...projectItemImport }, extraData)
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: ProjectItemImportPageProps
): IProjectItemImportPageProps => {
    return {
        match: ownProps.match,
        form: state.reduxForms.projectItemImport,
        lookups: state.projectItemImport.lookups,
        formState: state.projectItemImport.formState,
        formProperties: state.projectItemImport.formProperties,
        reduxFormState: state.reduxForms.formState.projectItemImport,
        validationFailures: state.projectItemImport.validationFailures,
        selectedItems: state.reduxForms.projectItemImport.selectedItems,
        categories:
            state.reduxForms.projectItemImport &&
            state.reduxForms.projectItemImport.templateCategories
                ? state.reduxForms.projectItemImport.templateCategories
                : [],

        projectId: state.projectSettingsPage.projectId,
        sourceProjectId: state.reduxForms.projectItemImport
            ? state.reduxForms.projectItemImport.projectId
            : undefined,

        history: ownProps.history,
        location: ownProps.location,

        projectItemImport: state.projectItemImport.formData,

        loading:
            state.projectItemImport.loadState &&
            state.projectItemImport.loadState.status == RequestState.Pending,
        loadSuccess:
            state.projectItemImport.loadState &&
            state.projectItemImport.loadState.status == RequestState.Success,
        loadFailure:
            state.projectItemImport.loadState &&
            state.projectItemImport.loadState.status == RequestState.Failure,

        saving:
            state.projectItemImport.saveState &&
            state.projectItemImport.saveState.status == RequestState.Pending,
        saveSuccess:
            state.projectItemImport.saveState &&
            state.projectItemImport.saveState.status == RequestState.Success,
        saveFailure:
            state.projectItemImport.saveState &&
            state.projectItemImport.saveState.status == RequestState.Failure,

        user: state.user.data,

        toggleItems: state.togglePage.items,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: ProjectItemImportPageProps
): IProjectItemImportPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        load: bindActionCreators(ProjectItemImportActions.Load, dispatch),
        clear: bindActionCreators(ProjectItemImportActions.Clear, dispatch),
        save: bindActionCreators(ProjectItemImportActions.Save, dispatch),

        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),

        toggleExpand: bindActionCreators(
            ToggleActions.ToggleExpanded,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<
    IProjectItemImportPageParams,
    ProjectItemImportPageProps
>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(
        ProjectItemImportPage
    ) as unknown as ComponentType<ProjectItemImportPageProps>
) as ComponentType<any>;
